import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Snackbar,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import api from "../../utils/api";

export default function SubscriptionsPage() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ client: "", plan: "", start_date: "", expiry_date: "" });
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ name: "", price: "", duration_days: 30, description: "" });
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [cancellationRequests, setCancellationRequests] = useState([]);

  // Load existing subscriptions
  const loadSubscriptions = async () => {
    try {
      const res = await api.get("/subscriptions/");
      setSubscriptions(res.data?.results || res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await api.get("/plans/");
      setPlans(res.data?.results || res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadCancellationRequests = async () => {
    try {
      const res = await api.get("/cancellation-requests/");
      setCancellationRequests(res.data?.results || res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const usersRes = await api.get("/non-staff-users/");
        setClients(usersRes?.data || usersRes || []);
        await loadPlans();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadSubscriptions();
    loadCancellationRequests();
  }, []);

  const handleCreate = async () => {
    try {
      await api.post("/subscriptions/", {
        user: form.client,
        plan: form.plan,
        start_date: form.start_date || undefined,
        expiry_date: form.expiry_date || undefined,
      });

      setSnackbar({ open: true, message: "Subscription created successfully!" });
      setDialogOpen(false);
      setForm({ client: "", plan: "", start_date: "", expiry_date: "" });
      loadSubscriptions();
    } catch (err) {
      console.error(err);
      const apiMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.response?.data?.user?.[0] ||
        err?.response?.data?.plan?.[0];
      setSnackbar({ open: true, message: apiMessage || "Failed to create subscription." });
    }
  };

  const resetPlanForm = () => {
    setPlanForm({ name: "", price: "", duration_days: 30, description: "" });
  };

  const handleCreatePlan = async () => {
    const name = planForm.name.trim();
    const priceValue = Number(planForm.price);
    const durationValue = Number(planForm.duration_days);

    if (!name) {
      setSnackbar({ open: true, message: "Plan name is required." });
      return;
    }
    if (!Number.isFinite(priceValue) || priceValue < 0) {
      setSnackbar({ open: true, message: "Enter a valid plan price." });
      return;
    }
    if (!Number.isFinite(durationValue) || durationValue <= 0) {
      setSnackbar({ open: true, message: "Enter a valid duration in days." });
      return;
    }
    try {
      setCreatingPlan(true);
      await api.post("/plans/", {
        name,
        price: priceValue.toFixed(2),
        duration_days: Math.floor(durationValue),
        description: planForm.description.trim() || "",
      });

      setSnackbar({ open: true, message: "Plan created successfully!" });
      setPlanDialogOpen(false);
      resetPlanForm();
      await loadPlans();
    } catch (err) {
      console.error(err);
      const apiMessage =
        err.response?.data?.name?.[0] ||
        err.response?.data?.price?.[0] ||
        err.response?.data?.duration_days?.[0] ||
        err.response?.data?.detail;
      setSnackbar({ open: true, message: apiMessage || "Failed to create plan." });
    } finally {
      setCreatingPlan(false);
    }
  };

  // ⭐ FIXED — These two functions **moved inside component**
  const handleApproveRequest = async (requestId) => {
    try {
      await api.post(`/cancellation-requests/${requestId}/approve/`);
      setSnackbar({ open: true, message: "Cancellation approved." });
      await loadSubscriptions();
      await loadCancellationRequests();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Failed to approve request." });
    }
  };

  const handleDenyRequest = async (requestId) => {
    try {
      await api.post(`/cancellation-requests/${requestId}/deny/`);
      setSnackbar({ open: true, message: "Cancellation denied." });
      await loadCancellationRequests();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: "Failed to deny request." });
    }
  };

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>
        Manage Subscriptions
      </Typography>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Create Subscription
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ p: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Expiry Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell>{sub.user_name}</TableCell>
                  <TableCell>{sub.plan_name}</TableCell>
                  <TableCell>{sub.status}</TableCell>
                  <TableCell>{new Date(sub.start_date).toLocaleDateString()}</TableCell>
                  <TableCell>{sub.expiry_date ? new Date(sub.expiry_date).toLocaleDateString() : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* CREATE SUBSCRIPTION MODAL */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Subscription</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={form.client}
              label="Client"
              onChange={(e) => setForm({ ...form, client: e.target.value })}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {`${c.first_name || ""} ${c.last_name || ""}`.trim() ||
                    c.username ||
                    c.email ||
                    `User #${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={form.plan}
              label="Plan"
              onChange={(e) => setForm({ ...form, plan: e.target.value })}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — £{parseFloat(p.price).toFixed(2)} / month
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={() => setPlanDialogOpen(true)}>
              Create Plan
            </Button>
          </Box>

          <TextField
            label="Start Date"
            type="date"
            fullWidth
            value={form.start_date}
            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Expiry Date (optional)"
            type="date"
            fullWidth
            value={form.expiry_date}
            onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      {/* CREATE PLAN MODAL */}
      <Dialog
        open={planDialogOpen}
        onClose={() => {
          if (!creatingPlan) {
            setPlanDialogOpen(false);
            resetPlanForm();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Plan</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Plan Name"
            fullWidth
            value={planForm.name}
            onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            label="Price (£)"
            type="number"
            fullWidth
            value={planForm.price}
            onChange={(e) => setPlanForm({ ...planForm, price: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: "0.01" }}
            required
          />
          <TextField
            label="Duration (days)"
            type="number"
            fullWidth
            value={planForm.duration_days}
            onChange={(e) => setPlanForm({ ...planForm, duration_days: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={planForm.description}
            onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (!creatingPlan) {
                setPlanDialogOpen(false);
                resetPlanForm();
              }
            }}
            disabled={creatingPlan}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreatePlan} disabled={creatingPlan}>
            {creatingPlan ? <CircularProgress size={20} /> : "Create Plan"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CANCELLATION REQUESTS */}
      <Box mt={6}>
        <Typography variant="h5" gutterBottom>
          Cancellation Requests
        </Typography>

        {cancellationRequests.length === 0 ? (
          <Typography color="text.secondary">No pending requests.</Typography>
        ) : (
          <Paper sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Plan Type</TableCell>
                  <TableCell>Requested At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {cancellationRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.user_first_name || "-"}</TableCell>
                    <TableCell>{request.user_last_name || "-"}</TableCell>
                    <TableCell>{request.user_username || request.user_name || "-"}</TableCell>
                    <TableCell>{request.plan_name}</TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleString()}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{request.status}</TableCell>

                    <TableCell align="right">
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => handleApproveRequest(request.id)}
                        disabled={request.status !== "pending"}
                      >
                        Approve
                      </Button>

                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => handleDenyRequest(request.id)}
                        disabled={request.status !== "pending"}
                      >
                        Deny
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    </Container>
  );
}
